import { Box, CircularProgress, Grid } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import { AIAgent } from 'components/AIAgent';
import { useAppSelector } from 'components/app/redux/store';

import {
  deleteSavedQuery,
  editQuery,
  getIssuesCsv,
  getIssuesParams,
  getSavedQuery,
  issueCanCreate,
  saveQuery,
} from '../../../../packages/common/api';
import ChatBot from '../../../../packages/common/shared-ui/src/components/ChatBot';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import { ButtonsTypes } from '../../../../packages/common/shared-ui/src/enums/commonEnums';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import {
  formatFilters,
  formatQueryFilters,
  formatQueryToSave,
  paramsToQuery,
  queryToParams,
  selectFilters,
} from '../../../../packages/common/utils/gridFiltersHelperNew';
import CommonPage from '../../CommonPage/CommonPage';
import GridFiltersModalNew from '../../GridFiltersModalNew/GridFiltersModalNew';
import GridFiltersPanelNew from '../../GridFiltersPanelNew/GridFiltersPanelNew';
import GridSaveQueryModal from '../../GridSaveQueryModal/GridSaveQueryModal';
import GridTotals from '../../GridTotals/GridTotals';
import IssuesTable from '../IssuesTable';
import { useGetIssueFiltersQuery, useGetIssuesQuery } from '../api/issues.api';

const IssuesContainer: React.FC = () => {
  const {
    data: filters,
    isLoading: isFiltersLoading,
    isSuccess: isFiltersLoaded,
  } = useGetIssueFiltersQuery();

  const getLocalString = useTranslation();
  const featureFlags = useAppSelector(state => state.configuration.features);
  const pageUseParams = useParams();
  const navigate = useNavigate();
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [openParamsModal, setOpenParamsModal] = useState(false);
  const [openSaveQueryModal, setOpenSaveQueryModal] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [queryData, setQueryData] = useState(null);
  const [totals, setTotals] = useState([]);

  const [currentParams, setCurrentParams] = useState(null);
  const [initialParams, setInitialParams] = useState(null);

  const [canCreate, setCanCreate] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [queryModalState, setQueryModalState] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    total: 0,
    pageSize: 100,
  });
  const [query, setQuery] = useQueryParams({
    groups: StringParam,
    filters: StringParam,
    totals: StringParam,
    sorts: StringParam,
    fields: StringParam,
    treeView: StringParam,
    limit: NumberParam,
    offset: NumberParam,
  });

  const {
    data: issues = {
      rows: [],
      fields: [],
      paging: {
        limit: 0,
        offset: 0,
        total: 0,
      },
    },
    isFetching: isIssuesLoading,
    isUninitialized: isIssuesInitialized,
  } = useGetIssuesQuery(
    {
      ...query,
      ...(pageUseParams.projectId && {
        project: pageUseParams.projectId,
      }),
    },
    { skip: !isFiltersLoaded },
  );

  useEffect(() => {
    const params = {};

    pageUseParams.queryId && (params.query = Number(pageUseParams.queryId));
    pageUseParams.projectId &&
      (params.project = Number(pageUseParams.projectId));

    async function getParamsData() {
      const { data } = await getIssuesParams(params);
      return data;
    }

    getParamsData().then(paramsData => {
      const params = formatFilters(paramsData);
      setInitialParams(params);
    });
  }, [pageUseParams.queryId, pageUseParams.projectId]);

  useEffect(() => {
    setQueryData(null);
    const ignore = false;
    async function getSavedQueries() {
      const { data: response } = await getSavedQuery({
        id: pageUseParams.queryId,
      });
      if (!ignore) {
        return response;
      }
    }
    if (pageUseParams.queryId) {
      getSavedQueries().then(res => {
        res && setQueryData(res);
      });
    }
  }, [pageUseParams.queryId]);
  useEffect(() => {
    if (!pageUseParams.queryId && initialParams) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams));
        setCurrentParams(initialParams);
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [initialParams, pageUseParams.queryId, setQuery]);
  useEffect(() => {
    if (pageUseParams.queryId && initialParams && queryData) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams, queryData.query));
        setCurrentParams(formatQueryFilters(initialParams, queryData.query));
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [
    initialParams,
    pageUseParams.queryId,
    setQuery,
    queryData,
    setCurrentParams,
  ]);

  useEffect(() => {
    let ignore = false;
    async function getCanCreate() {
      const params = {};
      pageUseParams.projectId && (params.project = pageUseParams.projectId);
      const { data: response } = await issueCanCreate(params);
      if (!ignore) {
        return response;
      }
    }

    getCanCreate().then(res => {
      setCanCreate(res);
    });

    return () => {
      ignore = true;
    };
  }, [pageUseParams.projectId]);

  // Update pagination when issues data changes
  useEffect(() => {
    if (issues.paging && issues.paging.limit > 0) {
      const currentPage = Math.floor(
        issues.paging.offset / issues.paging.limit,
      );
      setPagination({
        page: currentPage,
        total: issues.paging.total,
        pageSize: issues.paging.limit,
      });
    }
  }, [issues.paging]);

  useEffect(() => {
    if (issues.rows) {
      const totalRow = issues.rows.find(item => item.rowType === 'total');
      totalRow !== undefined && totalRow.columns && setTotals(totalRow.columns);
    }
  }, [issues.rows]);

  const handleSelectFilters = useCallback(
    data => {
      const result = selectFilters(data, currentParams);
      setCurrentParams(result);
      setQuery(paramsToQuery(result));
    },
    [currentParams, setQuery],
  );

  const handleParamsModalSave = useCallback(
    data => {
      setCurrentParams(data);
      setQuery(paramsToQuery(data));
      setOpenParamsModal(false);
    },
    [setQuery],
  );

  const handleToggleDelete = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await deleteSavedQuery({ id: pageUseParams.queryId });
          navigate('/issues');
          setOpenWarningModal(false);
          break;
        case 'delete':
          setOpenWarningModal(false);
          break;
      }
    },
    [pageUseParams.queryId, navigate],
  );

  const goCreateTask = useCallback(() => {
    let url = '/issues/create';
    const project = pageUseParams.projectId;
    if (project) {
      url = `/projects/${pageUseParams.projectId}/issues/create?projectId=${pageUseParams.projectId}`;
    }
    window.open(url);
  }, [pageUseParams.projectId]);

  const [contextMenuData, setContextMenuData] = useState({
    isSecondStepVisible: false,
    secondPopoverStepValue: null,
    data: [],
    currentRow: {
      services: {
        isApproved: '',
        isOvertime: false,
        canApprove: false,
        canEdit: false,
        isProjectClosed: false,
      },
    },
  });
  const downloadCsv = useCallback(() => {
    async function downloadIssuesCsv() {
      try {
        setDownloadLoading(true);
        const params = { ...query };
        //query = query.replaceAll(/ThisWeek|LastWeek|LastTwoWeeks|ThisMonth|LastMonth|ThisYear/gi, 'btw');
        //pageUseParams.queryId && (query = `query=${Number(pageUseParams.queryId)}&` + query);
        pageUseParams.projectId && (params.project = pageUseParams.projectId);
        //query = query.replaceAll('Today', 'eq');
        if (params) {
          const { data } = await getIssuesCsv(params);
          const url = window.URL.createObjectURL(
            new Blob(['\ufeff', data], { type: 'text/csv;charset=utf-8;' }),
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `issues-${dayjs().format('YYYY-MM-DD')}`,
          );
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          link.remove();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setDownloadLoading(false);
      }
    }
    downloadIssuesCsv();
  }, [pageUseParams.projectId, query]);

  const handleSaveQueryModalSave = useCallback(
    async modalData => {
      const value = formatQueryToSave(modalData, currentParams, 'issue');
      try {
        setSaveLoader(true);
        let response;
        if (queryModalState === 'edit') {
          response = await editQuery(pageUseParams.queryId, value);
        } else {
          response = await saveQuery(value);
        }
        if (response) {
          setSaveLoader(false);
          const { data: res } = response;
          if (pageUseParams.projectId) {
            window.open(
              `/projects/${pageUseParams.projectId}/issues/query/${res.id}`,
              '_self',
            );
          } else {
            window.open(`/issues/query/${res.id}`, '_self');
          }
        }
      } catch (error) {
        console.error(error);
      }
      setOpenSaveQueryModal(false);
    },
    [
      currentParams,
      queryModalState,
      pageUseParams.queryId,
      pageUseParams.projectId,
    ],
  );

  const handleParamsModalClose = useCallback(() => {
    setOpenParamsModal(false);
  }, []);

  const handleSaveQueryModalClose = useCallback(() => {
    setOpenSaveQueryModal(false);
  }, []);

  const handleWarningModalClose = useCallback(() => {
    setOpenWarningModal(false);
  }, []);

  const handleChangePagination = useCallback(
    (page: number) => {
      const pageSize = issues.paging?.limit || 100;
      setQuery(prev => ({ ...prev, offset: page * pageSize }));
    },
    [setQuery, issues.paging?.limit],
  );

  const handleChangeSorting = useCallback(
    newSortModel => {
      if (!newSortModel || newSortModel.length === 0) {
        setQuery(prev => ({ ...prev, sorts: undefined }));
        return;
      }

      const sortString = newSortModel
        .map(item => `${item.field};${item.sort}`)
        .join('$$');

      setQuery(prev => ({ ...prev, sorts: sortString }));
    },
    [setQuery],
  );

  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.ADD:
          goCreateTask();
          break;
        case ButtonsTypes.PARAMS:
          setOpenParamsModal(true);
          break;
        case ButtonsTypes.QUERY:
          setQueryModalState('create');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.EDIT_QUERY:
          setQueryModalState('edit');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.DELETE_QUERY:
          setOpenWarningModal(true);
          break;
        case ButtonsTypes.DOWNLOAD:
          downloadCsv();
          break;
      }
    },
    [downloadCsv, goCreateTask],
  );

  return (
    <>
      <WarningModal
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleToggleDelete}
        closeWarningModal={handleWarningModalClose}
        title={`${getLocalString('messages', 'deleteQuery')}?`}
      />
      {currentParams && (
        <GridFiltersModalNew
          handleSave={data => handleParamsModalSave(data)}
          handleClose={handleParamsModalClose}
          open={openParamsModal}
          data={currentParams}
        />
      )}
      <GridSaveQueryModal
        state={queryModalState}
        saveLoader={saveLoader}
        permission={'canSaveIssuesGlobalQueries'}
        queryData={queryData}
        open={openSaveQueryModal}
        handleSave={data => handleSaveQueryModalSave(data)}
        handleClose={handleSaveQueryModalClose}
      />
      <CommonPage
        loading={isIssuesInitialized}
        canCreate={canCreate}
        buttonEvent={handleButtonEvent}
        queryName={queryData?.name}
        queryUserId={queryData?.userId}
        downloadLoading={downloadLoading}
        component={'IssuesContainer'}
      >
        {currentParams && currentParams.filters?.selectedItems?.length > 0 && (
          <Box sx={{ marginTop: '10px' }}>
            <GridFiltersPanelNew
              data={currentParams.filters.selectedItems}
              handleCloseFilter={handleSelectFilters}
            ></GridFiltersPanelNew>
          </Box>
        )}
        {totals.length > 0 && <GridTotals data={totals}></GridTotals>}
        {issues.rows && (
          <Grid
            container
            wrap='nowrap'
            sx={{
              overflow: 'hidden',
              flexDirection: 'column',
              position: 'relative',
              marginTop: totals.length > 0 ? 0 : '20px',
            }}
          >
            {isFiltersLoading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 99,
                  position: 'absolute',
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
            {featureFlags.CHATBOT &&
              (featureFlags.CHATBOT_VOICE_INPUT ? <AIAgent /> : <ChatBot />)}

            <IssuesTable
              loading={isIssuesLoading}
              contextMenuData={contextMenuData}
              setContextMenuData={setContextMenuData}
              issuesColumnsData={issues.fields}
              issuesRowsData={issues.rows}
              onPageChange={handleChangePagination}
              pagination={pagination}
              changeSorting={handleChangeSorting}
            />
          </Grid>
        )}
      </CommonPage>
    </>
  );
};

export default IssuesContainer;
